/**
 * Validation utility functions
 */

/**
 * Validate email format
 */
export function validateEmail(email) {
  if (!email) return { valid: false, message: 'Email is required' }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email)) {
    return { valid: false, message: 'Please enter a valid email address' }
  }
  return { valid: true }
}

/**
 * Validate URL format
 */
export function validateURL(url) {
  if (!url) return { valid: false, message: 'URL is required' }
  try {
    // Auto-add https:// if missing
    let testUrl = url
    if (!url.match(/^https?:\/\//i)) {
      testUrl = 'https://' + url
    }
    new URL(testUrl)
    return { valid: true, normalized: testUrl }
  } catch (e) {
    return { valid: false, message: 'Please enter a valid URL' }
  }
}

/**
 * Validate phone number format
 */
export function validatePhone(phone) {
  if (!phone) return { valid: false, message: 'Phone number is required' }
  // Remove common formatting characters
  const cleaned = phone.replace(/[\s\-\(\)\.]/g, '')
  // Check if it's a valid phone number (at least 10 digits)
  const phoneRegex = /^\+?[\d]{10,15}$/
  if (!phoneRegex.test(cleaned)) {
    return { valid: false, message: 'Please enter a valid phone number' }
  }
  return { valid: true, normalized: cleaned }
}

/**
 * Validate hex color code
 */
export function validateHexColor(color) {
  if (!color) return { valid: false, message: 'Color is required' }
  const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/
  if (!hexRegex.test(color)) {
    return { valid: false, message: 'Please enter a valid hex color (e.g., #FF0000)' }
  }
  return { valid: true }
}

/**
 * Validate file size (in bytes)
 */
export function validateFileSize(file, maxSizeMB = 5) {
  if (!file) return { valid: false, message: 'File is required' }
  const maxSizeBytes = maxSizeMB * 1024 * 1024
  if (file.size > maxSizeBytes) {
    return { valid: false, message: `File size must be less than ${maxSizeMB}MB` }
  }
  return { valid: true }
}

/**
 * Validate file type
 */
export function validateFileType(file, allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']) {
  if (!file) return { valid: false, message: 'File is required' }
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, message: `File type must be one of: ${allowedTypes.join(', ')}` }
  }
  return { valid: true }
}

/**
 * Validate required field
 */
export function validateRequired(value, fieldName = 'This field') {
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    return { valid: false, message: `${fieldName} is required` }
  }
  return { valid: true }
}

/**
 * Validate minimum length
 */
export function validateMinLength(value, minLength, fieldName = 'This field') {
  if (!value || value.length < minLength) {
    return { valid: false, message: `${fieldName} must be at least ${minLength} characters` }
  }
  return { valid: true }
}

/**
 * Validate maximum length
 */
export function validateMaxLength(value, maxLength, fieldName = 'This field') {
  if (value && value.length > maxLength) {
    return { valid: false, message: `${fieldName} must be no more than ${maxLength} characters` }
  }
  return { valid: true }
}

/**
 * Normalize URL - add https:// if missing
 */
export function normalizeURL(url) {
  if (!url) return url
  if (url.match(/^https?:\/\//i)) {
    return url
  }
  return 'https://' + url
}

/**
 * Format phone number for display
 */
export function formatPhone(phone) {
  if (!phone) return phone
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  return phone
}






